import React, { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

const AdminLogin = () => {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <Loader2 className="text-[#C5A059] animate-spin" />
      </div>
    );
  }
  if (user && user.role === "admin") {
    const to = location.state?.from?.pathname || "/admin";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let msg;
      if (status === 401) {
        msg = "Identifiants incorrects.";
      } else if (status === 429) {
        msg = "Trop de tentatives. Patientez quelques minutes.";
      } else if (err.code === "ERR_NETWORK" || !err.response) {
        msg = "Connexion au serveur impossible. Vérifiez votre connexion internet.";
      } else {
        msg = formatApiErrorDetail(detail) || err.message || "Une erreur est survenue.";
      }
      setError(msg);
      // eslint-disable-next-line no-console
      console.error("Admin login error:", { status, detail, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-[#050505] px-6 py-20">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <img
            src="https://customer-assets.emergentagent.com/job_sneaker-hub-242/artifacts/44serdge_Suzel%20gold.png"
            alt="Suzel Holding"
            className="h-28 w-auto mx-auto mb-6"
            data-testid="admin-login-logo"
          />
          <p className="overline mb-3">Espace Administrateur</p>
          <p className="text-[#A1A1AA] text-sm mt-4">Gérez vos produits et messages en toute autonomie.</p>
        </div>

        <form onSubmit={submit} className="bg-[#0a0a0a] border border-white/5 p-8 space-y-6" data-testid="admin-login-form">
          <div>
            <label className="overline block mb-3">Email *</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full bg-transparent border-b border-white/10 focus:border-[#C5A059] outline-none py-3 text-white"
              placeholder="admin@suzelholding.com"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="overline block mb-3">Mot de passe *</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full bg-transparent border-b border-white/10 focus:border-[#C5A059] outline-none py-3 text-white"
              autoComplete="current-password"
            />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-gold w-full justify-center"
          >
            {loading ? <><Loader2 size={14} className="animate-spin" /> Connexion...</> : "Se connecter"}
          </button>
        </form>

        <p className="text-center text-xs text-white/40 mt-6 font-grotesk uppercase tracking-[0.2em]">
          Suzel Holding · Goma, RDC
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
